import axios, {AxiosError} from "axios";
import {getTokenSilently, logoutGlobal} from "../Auth";
import {config} from "../config";
import {UserLoginInterface} from "../interfaces/user.login.interface";
import { getDashboardLoginUrl } from '../utils/suiteManagerAuth';

const AUTH_USER_REFRESH_ACCESS_URL = `${config.baseUrl}${config.suffixApi}${config.versionApi}/auth/refresh-access`;

interface HttpServiceInterface {
  get<T = any>(url: string, params?: URLSearchParams): Promise<T>
}

interface SuiteManagerLogContext {
  email?: string;
  userId?: number | string;
  accessToken: string | null;
  refreshToken: string | null;
}

interface TokenMetadata {
  expiresAtIso: string | null;
  expiresInSeconds: number | null;
  isExpired: boolean;
  payloadPreview?: unknown;
}

class HttpService implements HttpServiceInterface {
  
  get<T = any>(url: string, params?: URLSearchParams): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.get<T>(url, {
        headers: await HttpService.getHeaders(),
        params: params
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  getResource<T = any>(url: string, params?: URLSearchParams): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.get<T>(url, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: params,
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  download<T = any>(url: string, params?: URLSearchParams): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.get<T>(url, {
        headers: await HttpService.getHeaders(),
        params: params,
        responseType: "blob"
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  delete<T = any>(url: string, params?: URLSearchParams): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.delete<T>(url, {
        headers: await HttpService.getHeaders(),
        params: params,
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  post<T = any>(url: string, params?: any): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.post<T>(url, params,{
        headers: await HttpService.getHeaders()
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  upload<T = any>(url: string, params?: any): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.post<T>(url, params,{
        headers: {"Content-Type": "multipart/form-data"}
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  put<T = any>(url: string, params?: any, queryParams?: URLSearchParams): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.put<T>(url, params,{
        headers: await HttpService.getHeaders(),
        params: queryParams
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  putResource<T = any>(url: string, params?: any, queryParams?: URLSearchParams): Promise<T> {
    return new Promise<T>(async (res, rej) => {
      axios.put<T>(url, params,{
        headers: {
          'Content-Type': 'application/json'
        },
        params: queryParams,
      }).then((response) => {
        res(response.data)
      }).catch((error: AxiosError) => {
        rej(error.response)
      })
    })
  }
  
  private static async getHeaders(): Promise<any> {
    const token = await HttpService.getToken();
    this.logSuiteManagerRefresh('getHeaders:resolvedToken', {
      authorizationType: sessionStorage.getItem('suiteManagerToken') ? 'SuiteManager' : 'Auth0',
      resolvedToken: token ?? null,
      resolvedTokenMetadata: this.getTokenMetadata(token ?? null),
    });

    return {
      Accept: '*/*',
      Authorization: `Bearer ${token}`,
      'X-FRONTSTEPS-Payments-UserType':  sessionStorage.getItem('suiteManagerToken') ? 'SuiteManager' : 'Auth0',
      'X-FRONTSTEPS-Payments-Userdata': sessionStorage.getItem('userData') || '',
      'Content-Type': 'application/json'
    }
  }
  
  private static async useRefreshToken(refreshToken: string): Promise<string> {
    this.logSuiteManagerRefresh('useRefreshToken:start', {
      requestedRefreshToken: refreshToken,
    });

    try {
      const suiteManagerToken = sessionStorage.getItem('suiteManagerToken');
      const response = await axios.post<UserLoginInterface>(
        AUTH_USER_REFRESH_ACCESS_URL,
        {refreshToken},
        {
          headers: {
            Authorization: `Bearer ${suiteManagerToken ?? ''}`,
            'X-FRONTSTEPS-Payments-UserType': 'SuiteManager',
          },
        },
      );
      const res = response.data;

      this.logSuiteManagerRefresh('useRefreshToken:responseReceived', {
        status: response.status,
        returnedAccessToken: res?.accessToken ?? null,
        returnedRefreshToken: res?.refreshToken ?? null,
        returnedAccessTokenMetadata: this.getTokenMetadata(res?.accessToken ?? null),
      });

      if (!res?.accessToken) {
        this.logSuiteManagerRefresh('useRefreshToken:missingAccessTokenInResponse', {
          responseData: res ?? null,
        });
        throw new Error('Suite Manager refresh response did not include an access token.');
      }

      if (!res?.refreshToken) {
        this.logSuiteManagerRefresh('useRefreshToken:missingRefreshTokenInResponse', {
          responseData: res ?? null,
        });
      }

      sessionStorage.setItem('suiteManagerToken', res.accessToken);

      if (res.refreshToken) {
        sessionStorage.setItem('suiteManagerRefresh', res.refreshToken);
      }

      this.logSuiteManagerRefresh('useRefreshToken:success', {
        storedAccessToken: sessionStorage.getItem('suiteManagerToken'),
        storedRefreshToken: sessionStorage.getItem('suiteManagerRefresh'),
        storedAccessTokenMetadata: this.getTokenMetadata(sessionStorage.getItem('suiteManagerToken')),
      });
      
      return res.accessToken;
    } catch (error) {
      this.logSuiteManagerRefresh('useRefreshToken:error', {
        requestedRefreshToken: refreshToken,
        error: this.serializeError(error),
      });
      throw error;
    }
  }
  
  private static async getToken(): Promise<string | undefined> {
    try {
      const suiteManagerToken = sessionStorage.getItem('suiteManagerToken')
      const suiteManagerRefresh = sessionStorage.getItem('suiteManagerRefresh')
      
      if (!!suiteManagerToken) {
        this.logSuiteManagerRefresh('getToken:suiteManagerTokenFound', {
          suiteManagerTokenMetadata: this.getTokenMetadata(suiteManagerToken),
          suiteManagerRefreshMetadata: this.getTokenMetadata(suiteManagerRefresh),
        });
        
        // non-expired access token is authenticated
        if (!this.tokenExpired(suiteManagerToken)) {
          this.logSuiteManagerRefresh('getToken:usingExistingAccessToken');
          return suiteManagerToken;
        } else {
          this.logSuiteManagerRefresh('getToken:accessTokenExpired');
          
          // if accessToken is expired, use refresh token
          if (!!suiteManagerRefresh) {
            this.logSuiteManagerRefresh('getToken:refreshTokenAvailable', {
              suiteManagerRefreshMetadata: this.getTokenMetadata(suiteManagerRefresh),
            });
            try {
              await this.useRefreshToken(suiteManagerRefresh); // accessToken is reset when using refresh token
              const newAccessToken = sessionStorage.getItem('suiteManagerToken');
              this.logSuiteManagerRefresh('getToken:refreshAttemptCompleted', {
                newAccessToken,
                newAccessTokenMetadata: this.getTokenMetadata(newAccessToken),
                newRefreshToken: sessionStorage.getItem('suiteManagerRefresh'),
              });
              if (!this.tokenExpired(newAccessToken)) {
                this.logSuiteManagerRefresh('getToken:usingRefreshedAccessToken');
                return newAccessToken || undefined;
              }
              this.logSuiteManagerRefresh('getToken:refreshedAccessTokenStillInvalid', {
                newAccessTokenMetadata: this.getTokenMetadata(newAccessToken),
              });
            } catch (err) {
              this.logSuiteManagerRefresh('getToken:refreshTokenFailed', {
                error: this.serializeError(err),
              });
              // failure to use refresh token is not authenticated
              logoutGlobal({ returnTo: getDashboardLoginUrl() });
              return;
            }
          }

          this.logSuiteManagerRefresh('getToken:refreshTokenMissing');
        }

        this.logSuiteManagerRefresh('getToken:returningOriginalTokenAfterDecision');
        return suiteManagerToken;
      }
      
      this.logSuiteManagerRefresh('getToken:noSuiteManagerTokenUsingAuth0');

      // Auth0 token
      const token = await getTokenSilently();
      if (token) {
        this.logSuiteManagerRefresh('getToken:auth0TokenResolved', {
          auth0TokenMetadata: this.getTokenMetadata(token),
        });
        return token
      } else {
        logoutGlobal({ returnTo: getDashboardLoginUrl() });
      }
    } catch (e) {
      this.logSuiteManagerRefresh('getToken:unexpectedFailure', {
        error: this.serializeError(e),
      });
      logoutGlobal({ returnTo: getDashboardLoginUrl() });
    }
  }
  private static getTokenExpirationTime(token: string, bufferInSeconds = 30): number | null {
    if (!token) {
      this.logSuiteManagerRefresh('getTokenExpirationTime:missingToken');
      return null;
    }
    
    const payload = token.split('.')[1];
    if (!payload) {
      this.logSuiteManagerRefresh('getTokenExpirationTime:invalidTokenFormat', {
        token,
      });
      return null;
    }
    
    try {
      const decodedPayload = JSON.parse(atob(payload));
      if (!decodedPayload.exp) {
        this.logSuiteManagerRefresh('getTokenExpirationTime:missingExpClaim', {
          token,
          decodedPayload,
        });
        return null;
      }
      
      // JWT exp is in seconds, convert it to milliseconds
      // Subtracting bufferTime in seconds (default 30)
      return (decodedPayload.exp - bufferInSeconds) * 1000;
    } catch (err) {
      this.logSuiteManagerRefresh('getTokenExpirationTime:decodeFailed', {
        token,
        error: this.serializeError(err),
      });
      return null;
    }
  }
  private static tokenExpired(token: string | null): boolean {
    if (!token) {
      this.logSuiteManagerRefresh('tokenExpired:missingToken');
      return true;
    }
    
    const tokenExpiration = this.getTokenExpirationTime(token);
    if (!tokenExpiration) {
      this.logSuiteManagerRefresh('tokenExpired:missingExpiration', {
        tokenMetadata: this.getTokenMetadata(token),
      });
      return true;
    }

    const expirationDate = new Date(tokenExpiration);
    const now = new Date();
    const isExpired = expirationDate < now;

    this.logSuiteManagerRefresh('tokenExpired:evaluated', {
      tokenMetadata: this.getTokenMetadata(token),
      expirationDateIso: expirationDate.toISOString(),
      nowIso: now.toISOString(),
      isExpired,
    });
    
    return isExpired
  }

  private static getSuiteManagerLogContext(): SuiteManagerLogContext {
    const rawUserData = sessionStorage.getItem('userData');
    let email: string | undefined;
    let userId: number | string | undefined;

    if (rawUserData) {
      try {
        const userData = JSON.parse(rawUserData);
        email = userData?.email;
        userId = userData?.userId;
      } catch (error) {
        console.error('[SuiteManagerRefresh]', {
          event: 'parseUserDataFailed',
          rawUserData,
          error,
        });
      }
    }

    return {
      email,
      userId,
      accessToken: sessionStorage.getItem('suiteManagerToken'),
      refreshToken: sessionStorage.getItem('suiteManagerRefresh'),
    };
  }

  private static logSuiteManagerRefresh(event: string, details?: Record<string, unknown>): void {
    console.info('[SuiteManagerRefresh]', {
      event,
      ...this.getSuiteManagerLogContext(),
      ...details,
    });
  }

  private static getTokenMetadata(token: string | null): TokenMetadata | null {
    if (!token) {
      return null;
    }

    const payload = token.split('.')[1];
    if (!payload) {
      return {
        expiresAtIso: null,
        expiresInSeconds: null,
        isExpired: true,
      };
    }

    try {
      const decodedPayload = JSON.parse(atob(payload));
      const exp = typeof decodedPayload?.exp === 'number' ? decodedPayload.exp : null;
      const nowInSeconds = Math.floor(Date.now() / 1000);

      return {
        expiresAtIso: exp ? new Date(exp * 1000).toISOString() : null,
        expiresInSeconds: exp ? exp - nowInSeconds : null,
        isExpired: exp ? exp <= nowInSeconds : true,
        payloadPreview: {
          iss: decodedPayload?.iss,
          sub: decodedPayload?.sub,
          aud: decodedPayload?.aud,
          exp: decodedPayload?.exp,
          iat: decodedPayload?.iat,
        },
      };
    } catch {
      return {
        expiresAtIso: null,
        expiresInSeconds: null,
        isExpired: true,
      };
    }
  }

  private static serializeError(error: unknown): unknown {
    if (axios.isAxiosError(error)) {
      return {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
          data: error.config?.data,
        },
      };
    }

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    return error;
  }
}

const httpService = new HttpService()

export default httpService
